import { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { ArrowRightOutlined, DownOutlined, UpOutlined } from "@ant-design/icons";

import { MainColor, SubColor } from "../../style/styleValue";
import useReveal, { revealStyle } from "./useReveal";

// Bepath 소개 > 격려의 글 (원문 그대로)
const paragraphs = [
  "소아암과 희귀질환은 그 희소성으로 인해 정확한 진단과 치료 방향 설정에 많은 어려움이 있어 왔습니다. 특히 병리학적 진단은 치료의 시작점이자 예후 예측의 핵심 요소임에도 불구하고, 증례의 부족과 표준화된 참고 자료의 제한으로 인해 전문가들조차 진단에 고심하는 경우가 적지 않습니다.",
  "이러한 상황에서 소아암·희귀질환지원사업단의 병리 아틀라스 구축 사업은 매우 시의적절하고 의미 있는 프로젝트입니다. 본 사업을 통해 구축되는 포괄적이고 체계적인 병리 이미지 데이터베이스는 단순한 자료 집합을 넘어, 정확한 진단을 위한 실질적 가이드이자 교육 자료로서 큰 가치를 지닐 것입니다.",
  "특히 디지털 병리학의 발전과 함께 고해상도 이미지, 면역조직화학 염색, 분자병리학적 정보가 통합된 아틀라스는 전국의 병리 전문의들이 희귀 증례를 접했을 때 신속하고 정확한 진단을 내리는 데 실질적인 도움을 줄 것으로 기대됩니다. 또한 차세대 병리 전문의 양성을 위한 교육 자원으로도 활용될 수 있어, 그 파급 효과는 현재를 넘어 미래까지 이어질 것입니다.",
  "소아병리연구회는 국내 소아 병리 분야의 발전과 전문가 네트워크 구축을 위해 노력해 온 학술단체로서, 본 아틀라스 구축 사업이 임상 현장에서 실질적으로 활용되고 지속적으로 발전할 수 있도록 적극 협력하고자 합니다.",
  "본 사업의 성공적인 완수와 소아암·희귀질환으로 고통받는 환아들에게 보다 정확한 진단과 최적의 치료가 제공될 수 있기를 진심으로 기원합니다.",
];

// 접힌 상태에서 보여줄 문단 수
const PREVIEW_COUNT = 2;

const EncouragementSection = () => {
  const ref = useReveal<HTMLElement>();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  const visibleParagraphs = expanded ? paragraphs : paragraphs.slice(0, PREVIEW_COUNT);

  return (
    <Wrapper ref={ref} id="encouragement">
      <div className="container">
        <Layout>
          <Intro className="reveal">
            <span className="eyebrow">Letter of Encouragement</span>
            <h2>격려의 글</h2>
            <p className="title">소아암·희귀질환 병리 아틀라스 구축 사업에 부쳐</p>

            <blockquote>
              정확한 진단을 위한 실질적 가이드이자{' '}
              <br />
              교육 자료로서 큰 가치를 지닐 것입니다.
            </blockquote>

            <Author>
              <div>
                <strong>김정선</strong>
                <span>대한병리학회 소아병리연구회 대표</span>
                <span>성균관의대 삼성서울병원 병리과 교수</span>
              </div>
            </Author>
          </Intro>

          <Letter className="reveal" style={{ "--reveal-delay": "0.15s" } as React.CSSProperties}>
            <span className="quote-mark" aria-hidden>“</span>
            <div className={`body ${expanded ? "expanded" : ""}`}>
              {visibleParagraphs.map((text, index) => (
                <p key={index}>{text}</p>
              ))}
            </div>

            {expanded && <div className="date">2026년 1월</div>}

            <Actions>
              <button type="button" className="toggle" onClick={() => setExpanded((prev) => !prev)}>
                {expanded ? <>접기 <UpOutlined /></> : <>전문 펼쳐보기 <DownOutlined /></>}
              </button>
              <button type="button" className="link" onClick={() => navigate("/encouragement")}>
                격려의 글 페이지 <ArrowRightOutlined />
              </button>
            </Actions>
          </Letter>
        </Layout>
      </div>
    </Wrapper>
  );
};

const Wrapper = styled.section`
  ${revealStyle}
  padding: 120px 0;
  background: linear-gradient(180deg, #fff 0%, #eef3f9 100%);

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }

  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  gap: 64px;
  align-items: start;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const Intro = styled.div`
  position: sticky;
  top: 100px;

  @media (max-width: 992px) {
    position: static;
  }

  .eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: ${SubColor};
    margin-bottom: 16px;

    &::before {
      content: "";
      width: 24px;
      height: 2px;
      border-radius: 2px;
      background: currentColor;
    }
  }

  h2 {
    font-size: clamp(1.6rem, 3vw, 2.3rem);
    font-weight: 700;
    letter-spacing: -0.03em;
    color: #111;
    margin: 0 0 8px;
  }

  .title {
    font-size: 1.05rem;
    color: #5b6472;
    margin: 0 0 40px;
    word-break: keep-all;
  }

  blockquote {
    margin: 0 0 40px;
    padding: 4px 0 4px 24px;
    border-left: 3px solid ${SubColor};
    font-size: clamp(1.2rem, 2vw, 1.5rem);
    font-weight: 600;
    line-height: 1.6;
    letter-spacing: -0.02em;
    color: ${MainColor};
    word-break: keep-all;

    @media (max-width: 576px) {
      br {
        display: none;
      }
    }
  }
`;

const Author = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  .avatar {
    flex: 0 0 56px;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    font-size: 1.3rem;
    font-weight: 700;
    color: #fff;
    background: linear-gradient(135deg, ${MainColor}, ${SubColor});
  }

  div:last-child {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  strong {
    font-size: 1.1rem;
    color: #111;
    letter-spacing: 0.1em;
  }

  span {
    font-size: 0.9rem;
    color: #6b7482;
  }
`;

const Letter = styled.article`
  position: relative;
  padding: 56px 56px 40px;
  background: #fff;
  border-radius: 20px;
  border: 1px solid #e3eaf3;
  box-shadow: 0 24px 60px rgba(23, 55, 95, 0.08);
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${MainColor}, ${SubColor});
  }

  .quote-mark {
    position: absolute;
    top: 8px;
    right: 32px;
    font-family: Georgia, serif;
    font-size: 9rem;
    line-height: 1;
    color: ${SubColor}1a;
    pointer-events: none;
  }

  .body {
    position: relative;

    /* 접힌 상태에서 마지막 문단을 흐리게 처리 */
    &:not(.expanded)::after {
      content: "";
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: 80px;
      background: linear-gradient(rgba(255, 255, 255, 0), #fff);
    }
  }

  p {
    font-size: 1rem;
    line-height: 1.9;
    color: #4a5568;
    margin: 0 0 20px;
    word-break: keep-all;
  }

  .date {
    margin-top: 8px;
    text-align: right;
    font-size: 0.95rem;
    color: #718096;
  }

  @media (max-width: 576px) {
    padding: 40px 24px 32px;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 12px;
  margin-top: 28px;
  padding-top: 24px;
  border-top: 1px solid #edf2f7;

  button {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 18px;
    border-radius: 8px;
    font-size: 0.95rem;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .toggle {
    border: 1px solid ${SubColor};
    background: #fff;
    color: ${SubColor};

    &:hover {
      background: ${SubColor};
      color: #fff;
    }
  }

  .link {
    border: none;
    background: transparent;
    color: #5b6472;

    &:hover {
      color: ${MainColor};
    }
  }
`;

export default EncouragementSection;
